import { type ReactNode, useEffect, useRef } from 'react';
import { Fade } from '@/components/ui/fade';
import { EmptyState } from '@/components/ui/empty-state';
import { useMinLoading } from '@/hooks/use-min-loading';
import type { FeedItem } from '@/components/comunicados/types';
import { useComunicadosFeed } from '../hooks/use-feed';
import { FeedItemCard } from './feed-item-card';
import { FeedListSkeleton } from './feed-list-skeleton';

const SHELL = 'mx-auto w-full max-w-[42rem] divide-y divide-white/[0.08]';
const SPINNER = 'py-md text-center text-xs text-text-tertiary';

function useScrollLoader(args: {
  hasNext: boolean;
  pending: boolean;
  fetchNext: () => void;
}) {
  const sentinel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinel.current;
    if (!node) return;
    const obs = new globalThis.IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry?.isIntersecting && args.hasNext && !args.pending) {
        args.fetchNext();
      }
    });
    obs.observe(node);
    return () => obs.disconnect();
  }, [args]);
  return sentinel;
}

type ContentProps = {
  items: FeedItem[];
  sentinel: React.RefObject<HTMLDivElement | null>;
  isFetchingNextPage: boolean;
};

function FeedContent({
  items,
  sentinel,
  isFetchingNextPage,
}: ContentProps): ReactNode {
  if (items.length === 0)
    return <EmptyState title="No hay comunicados todavía." />;
  return (
    <div className={SHELL}>
      {items.map((item) => (
        <FeedItemCard key={item.id} item={item} />
      ))}
      <div ref={sentinel} className="h-8" />
      {isFetchingNextPage && <p className={SPINNER}>Cargando más…</p>}
    </div>
  );
}

export function FeedList(): ReactNode {
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useComunicadosFeed();
  const sentinel = useScrollLoader({
    hasNext: !!hasNextPage,
    pending: isFetchingNextPage,
    fetchNext: () => void fetchNextPage(),
  });
  const minLoading = useMinLoading(isLoading);
  const showSkeleton = isLoading || minLoading;
  const items = data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Fade activeKey={showSkeleton ? 'skeleton' : 'data'}>
      {showSkeleton ? (
        <FeedListSkeleton />
      ) : (
        <FeedContent
          items={items}
          sentinel={sentinel}
          isFetchingNextPage={isFetchingNextPage}
        />
      )}
    </Fade>
  );
}
